import { SubmitHandler, useForm } from 'react-hook-form'
import zod from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, ButtonLoader, Button } from '@wellesley/ui'
import { FC } from 'react'

import { usePostApiV1AccountsPasswordChangeRequestMutation } from '../../../../authApi'
import { useDispatch } from 'react-redux'
import { authSlice } from '../../../../authSlice'
import CommonFormField from 'components/form/CommonFormField'
import { passwordValidator, maxPwdLength, pwdCaption } from '@wellesley/forms'
import { TooManyAttempt } from '../../../TooManyAttempt'
import { AppsLink } from '@wellesley/router'

const formSchema = zod
  .object({
    password: passwordValidator,
    newPassword: passwordValidator,
    confirmNewPassword: zod.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  })

export type ChangePasswordFormInput = zod.TypeOf<typeof formSchema>

type ChangePasswordFormProps = {
  onCancel: () => void
}

export const ChangePasswordForm: FC<ChangePasswordFormProps> = ({
  onCancel,
}) => {
  const dispatch = useDispatch()
  const { setChangePasswordStep } = authSlice.actions

  const form = useForm<ChangePasswordFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  })
  const { control, handleSubmit } = form

  const [requestChange, { isError, error, isLoading }] =
    usePostApiV1AccountsPasswordChangeRequestMutation()

  const onSubmit: SubmitHandler<ChangePasswordFormInput> = async (values) => {
    await requestChange({
      passwordChangeRequest: {
        currentPassword: values.password,
        newPassword: values.newPassword,
      },
    }).unwrap()
    dispatch(setChangePasswordStep('passCode'))
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col"
        onSubmit={(...args) => void handleSubmit(onSubmit)(...args)}
      >
        <div className="border-b border-gray-200 p-m">
          <h2 className="text-center text-lg font-semibold text-gray-700">
            Change password
          </h2>
        </div>
        <div className="flex flex-col gap-m px-m py-m">
          <TooManyAttempt
            message="The attempt was unsuccessful, please try again later"
            isError={isError}
            error={error}
          />
          <div className="flex flex-col gap-xxs">
            <CommonFormField
              type="password"
              control={control}
              name="password"
              displayName="Current password"
              maxChars={maxPwdLength}
              className="gap-xxs"
              inputClassName="rounded-m border border-gray-300 px-s py-xs text-base text-gray-900 outline-offset-[-1px]"
            />
            <AppsLink
              to="/forgot-password"
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              Forgot your password?
            </AppsLink>
          </div>
          <div className="h-px bg-gray-200" />
          <CommonFormField
            type="password"
            control={control}
            name="newPassword"
            displayName="New password"
            maxChars={maxPwdLength}
            description={pwdCaption}
            className="gap-xxs"
            inputClassName="rounded-m border border-gray-300 px-s py-xs text-base text-gray-900 outline-offset-[-1px]"
          />
          <CommonFormField
            type="password"
            control={control}
            name="confirmNewPassword"
            displayName="Confirm new password"
            maxChars={maxPwdLength}
            className="gap-xxs"
            inputClassName="rounded-m border border-gray-300 px-s py-xs text-base text-gray-900 outline-offset-[-1px]"
          />
          <p className="text-center text-base font-normal leading-5 text-gray-500">
            New sign-in will be required on all devices
          </p>
        </div>
        <div className="flex items-center justify-end gap-xs px-m pb-m">
          <Button
            type="button"
            variant="secondary"
            size="m"
            className="flex-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <ButtonLoader
            type="submit"
            size="m"
            variant="primary"
            isLoading={isLoading}
            className="flex-1"
          >
            Change password
          </ButtonLoader>
        </div>
      </form>
    </Form>
  )
}
